/**
 * Work experience list on brandProfiles/{uid}.workExperiences
 * Backward compatible: if missing, derive a single entry from company + timeAtCompany.
 */

export type BrandWorkExperienceEntry = {
  id: string;
  companyName: string;
  roleTitle: string;
  logoUrl?: string;
  startMonth: number;
  startYear: number;
  endMonth?: number | null;
  endYear?: number | null;
  /** When true, show "to Present" and ignore end dates */
  current?: boolean;
};

function startSortKey(e: BrandWorkExperienceEntry): number {
  return (e.startYear || 0) * 12 + (e.startMonth || 0);
}

/** End sort key for ordering: current roles sort last (most recent in career terms). */
function endSortKey(e: BrandWorkExperienceEntry): number {
  if (e.current) return Number.MAX_SAFE_INTEGER;
  const y = e.endYear ?? e.startYear ?? 0;
  const m = e.endMonth ?? e.startMonth ?? 0;
  return y * 12 + m;
}

/** Most recent first (reverse chronological): higher end date first, then higher start. */
export function sortWorkExperiencesReverseChronological(
  entries: BrandWorkExperienceEntry[],
): BrandWorkExperienceEntry[] {
  return [...entries].sort((a, b) => {
    const d = endSortKey(b) - endSortKey(a);
    if (d !== 0) return d;
    return startSortKey(b) - startSortKey(a);
  });
}

export function normalizeWorkExperienceEntry(raw: Record<string, unknown>, fallbackId: string): BrandWorkExperienceEntry {
  return {
    id: typeof raw.id === 'string' ? raw.id : fallbackId,
    companyName: String(raw.companyName ?? '').trim(),
    roleTitle: String(raw.roleTitle ?? '').trim(),
    logoUrl: typeof raw.logoUrl === 'string' ? raw.logoUrl.trim() : undefined,
    startMonth: Math.min(12, Math.max(1, Number(raw.startMonth) || 1)),
    startYear: Number(raw.startYear) || new Date().getFullYear(),
    endMonth: raw.endMonth != null ? Number(raw.endMonth) : null,
    endYear: raw.endYear != null ? Number(raw.endYear) : null,
    current: raw.current === true,
  };
}

/**
 * Display list: use workExperiences if present, else one synthetic row from legacy company fields.
 */
export function getWorkExperiencesForDisplay(brandProfile: Record<string, unknown> | null | undefined): BrandWorkExperienceEntry[] {
  if (!brandProfile) return [];
  const raw = brandProfile.workExperiences;
  if (Array.isArray(raw) && raw.length > 0) {
    return sortWorkExperiencesReverseChronological(
      raw.map((item, i) => normalizeWorkExperienceEntry(item as Record<string, unknown>, `row-${i}`)),
    );
  }
  const company = (brandProfile.company || {}) as Record<string, unknown>;
  const timeAt = (company.timeAtCompany || {}) as Record<string, unknown>;
  const name = String(company.name ?? '').trim();
  const roleTitle = String(company.roleTitle ?? '').trim();
  if (!name && !roleTitle) return [];
  const startMonth = Math.min(12, Math.max(1, Number(timeAt.startMonth) || 1));
  const startYear = Number(timeAt.startYear) || new Date().getFullYear();
  return [
    {
      id: 'legacy',
      companyName: name,
      roleTitle,
      logoUrl: typeof company.logoUrl === 'string' ? company.logoUrl : undefined,
      startMonth,
      startYear,
      current: true,
    },
  ];
}

/** Primary job for identity card / company sync: prefer current role, else most recent row. */
export function pickPrimaryWorkExperience(sorted: BrandWorkExperienceEntry[]): BrandWorkExperienceEntry | null {
  if (!sorted.length) return null;
  const current = sorted.find((e) => e.current);
  return current || sorted[0];
}

export function formatExperienceDateRange(
  e: BrandWorkExperienceEntry,
  monthNames: string[],
): string {
  const sm = e.startMonth >= 1 && e.startMonth <= 12 ? monthNames[e.startMonth - 1] : '';
  const start = sm && e.startYear ? `${sm} ${e.startYear}` : String(e.startYear || '');
  if (e.current) {
    return start ? `${start} to Present` : 'Present';
  }
  const em = e.endMonth && e.endMonth >= 1 && e.endMonth <= 12 ? monthNames[e.endMonth - 1] : '';
  const end =
    em && e.endYear ? `${em} ${e.endYear}` : e.endYear ? String(e.endYear) : '';
  if (start && end) return `${start} – ${end}`;
  if (start) return start;
  return end || '';
}
